import { CameraIcon } from "@heroicons/react/24/outline";

import { GetQuoteType } from "../../LeadInformation/QuoteCalculationCard";
import ClickToCopy from "../../../shared/ClickToCopy";
import { LeadSource, QuoteTabType, QuoteType } from "../../../../models/Lead";
import { ITableQuote } from "../../../../models/Quote";
import classNames from "../../../../utils/classNames";
import PriceFormat from "../../../../utils/priceFormat";
import { colorMap, QUOTE_CALL_STAGES } from "../QuoteCallStageFilter";
import { QUOTE_TYPE_TABS } from "../QuoteTypeTabs";
import { QUOTE_STAGES, quoteStageColorMap } from "../QuoteStageFilter";
import { getActionMeta } from "../../LeadActions";
import Tooltip from "../../../shared/Tooltip";
import AgeTimer from "../../../shared/Cells/AgeCell";
import formatDateTime from "../../../../utils/formatDateTime";
import { useAuth } from "../../../../contexts/AuthContext";
import { wilmaSAVCustomerId } from "../../../../config";

const parseOffer = (value?: string | null) => {
  if (!value) return 0;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export function QuoteVehicleCell({ quote }: { quote: ITableQuote }) {
  const vehicle = quote.vehicle;
  if (!vehicle?.vrm) return <span className="text-gray-400 italic">No vehicle</span>;

  const year = vehicle.dvlaYearOfManufacture ?? vehicle.modelYear ?? null;

  return (
    <div className="flex gap-2 items-center">
      <span className="shrink-0">
        {vehicle.vehicleImageUrl ? (
          <img className="w-20 h-14 object-contain rounded" src={vehicle.vehicleImageUrl} />
        ) : (
          <CameraIcon className="w-10 mx-5 text-gray-400" />
        )}
      </span>
      <div className="flex flex-col text-xs">
        <ClickToCopy title="VRM" text={vehicle.vrm} />
        <span className="font-medium text-gray-900">
          {year && <span className="text-gray-500 mr-1">{year}</span>}
          {vehicle.combinedMake ?? ""}
        </span>
        <span className="text-gray-600">{vehicle.modelRangeDescription ?? ""}</span>
        <span className="text-gray-400">{vehicle.fuelType ?? ""} {vehicle.transmission ?? ""}</span>
      </div>
    </div>
  );
}

export function QuoteCustomerCell({ quote }: { quote: ITableQuote }) {
  const contact = quote.contact;
  const vehicle = quote.vehicle;

  if (!contact || contact.fullname === "System No Contact") {
    return <span className="text-gray-400">No contact</span>;
  }

  const address = [vehicle?.line1, vehicle?.postcode].filter(Boolean).join(", ");

  return (
    <div className="flex flex-col text-xs gap-0.5">
      <span className="font-semibold text-gray-900">{contact.fullname}</span>
      <ClickToCopy text={contact.telephone1} />
      {address && <span className="text-gray-500 truncate max-w-[180px]" title={address}>{address}</span>}
    </div>
  );
}

export function QuotePriceCell({ value }: { value: number | null | undefined }) {
  if (value == null) return <span className="font-semibold text-gray-500 text-sm">—</span>;
  return <span className="font-semibold text-gray-900 text-sm">{PriceFormat(value)}</span>;
}

export function QuoteCalcTypeCell({ quoteType }: { quoteType: QuoteType | null | undefined }) {
  if (quoteType == null) return <span className="text-xs text-gray-400">—</span>;

  const colorMap: Record<number, string> = {
    [QuoteType.ScrapExport]: "bg-blue-50 text-blue-700",
    [QuoteType.Salvage]: "bg-amber-50 text-amber-700",
    [QuoteType.SmartPrice]: "bg-purple-50 text-purple-700",
  };

  return (
    <span className={classNames("inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold", colorMap[quoteType] ?? "bg-gray-100 text-gray-600")}>
      {GetQuoteType(quoteType) ?? "Unknown"}
    </span>
  );
}

export function QuoteTradeValueCell({ value }: { value: number | null | undefined }) {
  if (value == null) return <span className="text-xs text-gray-400">—</span>;
  return <span className="text-xs font-semibold text-gray-900">{PriceFormat(value)}</span>;
}

export function QuotePartsSalvageCell({ quote }: { quote: ITableQuote }) {
  const vehicle = quote.vehicle;
  const ewwEngine = parseOffer(vehicle?.enginesWorldwideEngineOffer);
  const ewwVfe = parseOffer(vehicle?.enginesWorldwideVfeOffer);
  const vlaEngine = parseOffer(vehicle?.vlaEngineOffer);
  const catValue = quote.catalyticAveragePrice ?? 0;

  const total = ewwEngine + ewwVfe + vlaEngine + catValue;
  if (total === 0) return <span className="text-xs text-gray-400">—</span>;

  return (
    <div className="flex flex-col text-xs">
      <span className="font-semibold text-emerald-700">{PriceFormat(total)}</span>
      <div className="flex flex-col text-[10px] text-gray-400 leading-tight mt-0.5">
        {(ewwEngine > 0 || ewwVfe > 0) && <span>EWW: {PriceFormat(ewwEngine + ewwVfe)}</span>}
        {vlaEngine > 0 && <span>VLA: {PriceFormat(vlaEngine)}</span>}
        {catValue > 0 && <span>Cat: {PriceFormat(catValue)}</span>}
      </div>
    </div>
  );
}

export default function QuoteCallStageCell({ quote }: { quote: ITableQuote }) {
  const callListStatus = quote.callListStatus;
  const stage = QUOTE_CALL_STAGES.find((s) => s.callListStatus === callListStatus);
  if (!stage) return <span className="text-xs text-gray-400">—</span>;
  const c = colorMap[stage.color];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full ${c.bg} ${c.text}`}>
      {stage.label}
    </span>
  );
}

export function QuoteStageCell({ quote }: { quote: ITableQuote }) {
  const quoteStage = quote.quoteStep;
  const stage = QUOTE_STAGES.find((s) => s.stage === quoteStage);
  if (!stage) return <span className="text-xs text-gray-400">—</span>;
  const c = quoteStageColorMap[stage.color];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full ${c.bg} ${c.text}`}>
      {stage.label}
    </span>
  );
}

export function QuoteActionCell({ quote }: { quote: ITableQuote }) {
  const quoteAction = quote.action;
  if(!quoteAction) return <span className="text-xs text-gray-400">No Action</span>;

  const meta = getActionMeta(quoteAction.actionType);
  const Icon = meta.icon;

  return (
    <Tooltip content={quoteAction.description ?? "No description"} position="left">
      <span
        className={`relative inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${meta.badgeClass} group/tooltip`}
      >
        <Icon className="h-4 w-4 text-gray-400" /> 
        <span className="ml-1">{formatDateTime(quoteAction.created)}</span>
      </span>
    </Tooltip>
  );
}

export function QuoteTabTypeCell({
  quote,
  onQuoteTabTypeChange,
}: {
  quote: ITableQuote;
  onQuoteTabTypeChange: (leadId: string, quoteTabType: QuoteTabType) => void;
}) {
  const { customerId } = useAuth();
  // Only SAV allocates ATF, so only SAV can move a quote onto the ATF tab.
  const isSavCustomer = !!customerId && customerId === wilmaSAVCustomerId;

  const isValidQuoteTabType = Object.values(QuoteTabType)
    .filter((value) => typeof value === "number")
    .includes(quote.quoteTabType as unknown as number);
  const selectedQuoteTabType = isValidQuoteTabType ? quote.quoteTabType : QuoteTabType.Quotes;

  const tabs = QUOTE_TYPE_TABS.filter(
    (tab) => tab.key !== QuoteTabType.Atf || isSavCustomer || selectedQuoteTabType === QuoteTabType.Atf
  );

  return (
    <select
      value={String(selectedQuoteTabType)}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        e.stopPropagation();
        const quoteTabType = Number(e.target.value) as QuoteTabType;
        onQuoteTabTypeChange(quote.id, quoteTabType);
      }}
      className="w-full min-w-[150px] rounded border-gray-300 text-xs focus:border-primary-500 focus:ring-primary-500"
    >
      {tabs.map((tab) => (
        <option key={tab.key} value={String(tab.key)}>
          {tab.label}
        </option>
      ))}
    </select>
  );
}

export function QuoteSourceCell({ quote }: { quote: ITableQuote }) {
  const source = quote.source;
  const website = quote.website;
  const label = website != null ? LeadSource[website] ?? source ?? "Unknown" : source ?? "—";
  return <span className="text-xs text-gray-700">{label}</span>;
}

export function QuoteAgeCell({ created }: { created: string }) {
  return <AgeTimer created={created} />;
}
